/**
 * RecordAdjustmentDialog — reusable dialog for recording an inventory adjustment
 * at a specific SKU + warehouse + date.
 *
 * Writes to inventory_adjustments via POST /api/stock-levels/set-true-stock.
 * Does NOT handle new batch receipts — those go through RecordReceiptDialog.
 *
 * Props:
 *   - open, onClose: dialog visibility control
 *   - sku, warehouseCode, currentQuantity: context for the adjustment (read-only)
 *   - brandId: required for the backend's scoping
 *   - onSuccess?(result): called after a successful write; parent can refetch
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { setTrueStock, formatNumber } from '../../services/inventoryService';

const CHANGE_TYPES = [
  { value: 'manual',         label: 'Manual Correction' },
  { value: 'physical_count', label: 'Physical Count' },
  { value: 'damage',         label: 'Damage' },
  { value: 'loss',           label: 'Loss' },
  { value: 'found',          label: 'Found / Surplus' },
  { value: 'expired',        label: 'Expired' },
];

function RecordAdjustmentDialog({
  open,
  onClose,
  sku,
  warehouseCode,
  currentQuantity,
  brandId,
  onSuccess,
}) {
  const todayStr = () => new Date().toISOString().slice(0, 10);

  const [effectiveDate, setEffectiveDate] = useState(todayStr);
  const [newQuantity, setNewQuantity] = useState('');
  const [changeType, setChangeType] = useState('manual');
  const [batchNumber, setBatchNumber] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset form whenever the dialog opens — each open should be a fresh entry
  useEffect(() => {
    if (open) {
      setEffectiveDate(todayStr());
      setNewQuantity('');
      setChangeType('manual');
      setBatchNumber('');
      setReason('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    const newQtyParsed = parseFloat(newQuantity);
    if (newQuantity === '' || isNaN(newQtyParsed)) {
      setError('New Quantity is required');
      return;
    }
    if (newQtyParsed < 0) {
      setError('New Quantity cannot be negative');
      return;
    }
    if (!brandId) {
      setError('Brand ID is missing');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const result = await setTrueStock({
        sku,
        warehouse_code: warehouseCode,
        effective_date: effectiveDate,
        true_quantity: newQtyParsed,
        brand_id: brandId,
        reason: reason.trim(),
        adjustment_type: changeType,
        batch_number: batchNumber.trim(),
      });
      if (onSuccess) onSuccess(result);
      onClose();
    } catch (err) {
      console.error('Failed to record adjustment:', err);
      setError(err.response?.data?.error || err.message || 'Failed to record adjustment');
    } finally {
      setSubmitting(false);
    }
  };

  const delta = newQuantity !== '' && !isNaN(parseFloat(newQuantity))
    ? parseFloat(newQuantity) - (currentQuantity || 0)
    : null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Record Adjustment</Typography>
          <Typography variant="body2" color="text.secondary">
            {sku} @ {warehouseCode}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
            <Typography variant="caption" color="text.secondary">Current system stock</Typography>
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              {formatNumber(currentQuantity || 0)}
            </Typography>
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Change Type</InputLabel>
            <Select
              value={changeType}
              label="Change Type"
              onChange={(e) => setChangeType(e.target.value)}
              disabled={submitting}
            >
              {CHANGE_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            type="date"
            label="Effective Date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={submitting}
            helperText="Date this adjustment applies to (typically the date it was observed/counted)"
          />

          <TextField
            fullWidth
            size="small"
            type="number"
            label="New Quantity"
            value={newQuantity}
            onChange={(e) => setNewQuantity(e.target.value)}
            placeholder={`Current: ${formatNumber(currentQuantity || 0)}`}
            disabled={submitting}
            helperText="Stock level at this warehouse after this adjustment"
            inputProps={{ min: 0 }}
          />

          {delta !== null && delta !== 0 && (
            <Box sx={{
              p: 1.5,
              bgcolor: delta > 0 ? 'success.50' : 'error.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: delta > 0 ? 'success.200' : 'error.200',
            }}>
              <Typography variant="caption" color="text.secondary">Adjustment</Typography>
              <Typography variant="body1" fontWeight="medium" color={delta > 0 ? 'success.main' : 'error.main'}>
                {delta > 0 ? '+' : ''}{formatNumber(delta)}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            size="small"
            label="Batch Number (optional)"
            value={batchNumber}
            onChange={(e) => setBatchNumber(e.target.value)}
            placeholder="Leave blank if the adjustment applies to total warehouse stock"
            disabled={submitting}
            helperText="Which batch is this adjustment for? Optional."
          />

          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. warehouse recount, water damage, expired, system drift, etc."
            disabled={submitting}
            helperText="Recommended — captured on the audit trail"
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || newQuantity === ''}
          color="warning"
        >
          {submitting && <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />}
          Record Adjustment
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RecordAdjustmentDialog;
